import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { APP_ICONS } from "../../assets";
import appcolors from "../../constants/Colors";
import { getAllChatsByConversatioId } from "../../services/adminUserApiFn";
import { useTranslation } from "react-i18next";

const style = {
  incoming: {
    p: "8px 10px",
    backgroundColor: "#F1F1F1",
    borderRadius: "8px",
    alignSelf: "flex-start",
    maxWidth: "70%",
    wordBreak: "break-word",
  },
  outgoing: {
    p: "8px 10px",
    color: appcolors.white,
    backgroundColor: appcolors.primary,
    borderRadius: "8px",
    alignSelf: "flex-end",
    maxWidth: "70%",
    wordBreak: "break-word",
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      "& .MuiOutlinedInput-notchedOutline": {
        border: `2px solid #F3F3F3`,
        boxShadow: "none",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        border: `2px solid #F3F3F3`,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: `2px solid #F3F3F3`,
      },
    },
  },
};

function MessageBox({ socket }) {
  const { t } = useTranslation(["common"]);
  const [chatLoading, setChatLoading] = useState(false);
  const [error, setError] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const active = useSelector((state) => state.activeChat.selectedChat);
  const userData = useSelector((state) => state.userData.user);
  const messageContainerRef = useRef(null);

  useEffect(() => {
    if (active) {
      socket.emit("resetUnreadCount", {
        conversationId: active.conversationId,
        userId: userData._id,
        covsersationType: active.type,
      });
    }
  }, [active, socket]);

  useEffect(() => {
    if (active) {
      fetchAllChats();
    }
  }, [active]);

  useEffect(() => {
    if (active) {
      socket.emit("joinConversationRoom", {
        conversationId: active?.conversationId,
      });

      const handleReceiveMessage = (newMessage) => {
        if (newMessage.conversationId === active?.conversationId) {
          setMessages((prev) => [...prev, newMessage]);
        }
      };

      socket.on("receiveMessage", handleReceiveMessage);

      return () => {
        socket.off("receiveMessage", handleReceiveMessage);
      };
    }
  }, [socket, active]);

  useEffect(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop =
        messageContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = () => {
    if (error) return;
    if (!message.trim()) return;

    socket.emit("sendMessage", {
      conversationId: active.conversationId,
      senderId: userData?._id,
      receiverId: active?.receiverId,
      senderRole: active?.senderRole,
      receiverRole: active?.receiverRole,
      message,
      conversationType: active?.type,
    });

    setMessages((prev) => [
      ...prev,
      {
        conversationId: active.conversationId,
        senderId: userData?._id,
        receiverId: active?.receiverId,
        senderRole: active?.senderRole,
        receiverRole: active?.receiverRole,
        message,
      },
    ]);
    setMessage("");
  };

  const fetchAllChats = async () => {
    try {
      setChatLoading(true);
      const response = await getAllChatsByConversatioId(active?.conversationId);
      if (response.data.status === "success") {
        setMessages(response.data.data);
      } else {
        setError(true);
      }
    } catch (error) {
      setError(true);
    } finally {
      setError(false);
      setChatLoading(false);
    }
  };

  // Render Functions
  const renderBoxHeader = (image, name, status) => {
    return (
      <Stack direction={"row"} alignItems={"center"} gap={2}>
        <Avatar
          sx={{ borderRadius: "25%" }}
          src={image}
          alt={`${name}-image`}
        />
        <Box>
          <Typography
            variant="body1"
            fontSize={"1rem"}
            fontWeight={"bold"}
            fontFamily={"Metropolis"}
          >
            {name}
          </Typography>
          <Stack direction={"row"} alignItems={"center"} gap={0.5}>
            <FiberManualRecordIcon
              htmlColor="#68D391"
              sx={{ fontSize: "0.875rem" }}
            />
            <Typography variant="body2" fontSize={"0.75rem"}>
              {t("online")}
            </Typography>
          </Stack>
        </Box>
      </Stack>
    );
  };

  const renderMessages = () => (
    <Stack
      p={1}
      gap={1}
      height={"55vh"}
      overflow={"auto"}
      ref={messageContainerRef}
      sx={{
        "&::-webkit-scrollbar": {
          width: "4px",
          height: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: appcolors.primary,
          borderRadius: "8px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: appcolors.secondary,
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: appcolors.lightGrey,
        },
      }}
    >
      {messages?.map((mes, ind) => (
        <Box
          key={ind}
          sx={mes.senderId === userData?._id ? style.outgoing : style.incoming}
          display={"flex"}
          flexDirection={"column"}
        >
          <Typography variant="body2">{mes.message}</Typography>
          <Typography
            fontSize={"0.65rem"}
            alignSelf={
              mes.senderId === userData?._id ? "flex-end" : "flex-start"
            }
          >
            {moment(mes.createdAt).format("MM/DD/YY hh:mm a")}
          </Typography>
        </Box>
      ))}
    </Stack>
  );

  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: "10px",
        position: "relative",
        height: "100%",
      }}
    >
      {chatLoading ? (
        <Stack height={"100%"} justifyContent={"center"} alignItems={"center"}>
          <CircularProgress />
        </Stack>
      ) : chatLoading === false && active === null ? (
        <Typography variant="body1" textAlign="center" mt={4}>
          {t("noConversationSelected")}
        </Typography>
      ) : chatLoading === false && active ? (
        <>
          {renderBoxHeader(active?.image, active?.name, true)}
          <Divider sx={{ my: 1 }} />
          {renderMessages()}

          <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
            <TextField
              sx={style.searchField}
              fullWidth
              placeholder={t("typeMessage")}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              InputProps={{
                // startAdornment: (
                //   <Box mr={1}>
                //     <AttachFileIcon sx={{ transform: "rotate(45deg)" }} />
                //   </Box>
                // ),
                endAdornment: (
                  <Box ml={1} sx={{ cursor: "pointer" }} onClick={sendMessage}>
                    <img src={APP_ICONS?.sendIcon} alt="send-icon" width={20} />
                  </Box>
                ),
              }}
            />
          </Box>
        </>
      ) : null}
    </Paper>
  );
}

export default MessageBox;
